import { useEffect, useState } from "react";
import Topbar from "@/components/Topbar";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { EnvelopeSimple, EnvelopeOpen, ArrowsClockwise, CheckCircle, ArrowUUpLeft, PaperPlaneTilt } from "@phosphor-icons/react";

/**
 * v3.2.45 · Admin contact-form inbox.
 *
 * Lists every submission from the landing "Get in touch" form, newest
 * first. Filters by status (open / handled / all). Row expander shows
 * the full message. "Mark handled" toggles the `handled` flag; a
 * "Reply" button opens a mailto: with the submitter's address.
 */
export default function AdminContactInboxPage() {
  const [rows, setRows]         = useState([]);
  const [counts, setCounts]     = useState({ open: 0, handled: 0, total: 0 });
  const [status, setStatus]     = useState("open");
  const [busy, setBusy]         = useState(false);
  const [expanded, setExpanded] = useState(new Set());
  // v3.2.59 · In-app reply drafts + send state, keyed by submission id.
  const [drafts, setDrafts]   = useState({});
  const [sending, setSending] = useState(new Set());

  const setDraft = (id, val) => setDrafts((d) => ({ ...d, [id]: val }));

  const sendReply = async (r) => {
    const body = (drafts[r.id] || "").trim();
    if (body.length < 2) { toast.error("Please write a reply first."); return; }
    setSending((s) => new Set(s).add(r.id));
    try {
      await api.post(`/admin/contact/${r.id}/reply`, { body });
      toast.success(`Reply sent to ${r.email}.`);
      setDraft(r.id, "");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Send failed");
    } finally {
      setSending((s) => { const n = new Set(s); n.delete(r.id); return n; });
    }
  };

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/admin/contact", { params: { status } });
      setRows(data.rows || []);
      setCounts(data.counts || { open: 0, handled: 0, total: 0 });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Load failed");
    } finally { setBusy(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const toggle = (id) => {
    const next = new Set(expanded);
    next.has(id) ? next.delete(id) : next.add(id);
    setExpanded(next);
  };

  const markHandled = async (row) => {
    try {
      const { data } = await api.post(`/admin/contact/${row.id}/handled`);
      toast.success(data.handled ? "Marked handled." : "Reopened.");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Update failed");
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toISOString().replace("T", " ").slice(0, 16) + " UTC";
    } catch { return iso; }
  };

  return (
    <div className="industrial min-h-screen bg-[#0A0C10]">
      <Topbar active="admin-contact" />
      <div className="max-w-6xl mx-auto px-6 md:px-10 py-10">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="label-tag">// admin · contact inbox</div>
            <h1 className="mt-2 font-display text-4xl md:text-5xl font-black tracking-tighter"
                data-testid="contact-inbox-heading">
              Contact inbox
            </h1>
            <p className="mt-2 text-sm text-gray-400 max-w-xl">
              Every submission from the landing "Get in touch" form.
              Also emailed to <span className="font-mono text-white">SUPPORT_EMAIL</span> —
              this page is the durable copy.
            </p>
          </div>
          <button
            onClick={load}
            disabled={busy}
            className="btn-ghost inline-flex items-center gap-2 text-xs py-2 px-3 disabled:opacity-50"
            data-testid="contact-inbox-refresh"
          >
            <ArrowsClockwise size={14} weight="bold" className={busy ? "animate-spin" : ""} />
            Refresh
          </button>
        </div>

        {/* Status tabs */}
        <div className="mt-8 flex items-center gap-2 border-b border-white/10">
          {[
            { key: "open",    label: "Open",    count: counts.open,    icon: EnvelopeSimple },
            { key: "handled", label: "Handled", count: counts.handled, icon: EnvelopeOpen },
            { key: "all",     label: "All",     count: counts.total,   icon: null },
          ].map((t) => (
            <button
              key={t.key}
              onClick={() => setStatus(t.key)}
              className={`px-4 py-3 text-sm font-mono uppercase tracking-wider border-b-2 -mb-px transition-colors inline-flex items-center gap-2
                ${status === t.key
                    ? "border-[#00E5FF] text-[#00E5FF]"
                    : "border-transparent text-gray-500 hover:text-gray-300"}`}
              data-testid={`contact-inbox-tab-${t.key}`}
            >
              {t.icon && <t.icon size={14} weight="duotone" />}
              {t.label} <span className="text-[10px] text-gray-500">({t.count})</span>
            </button>
          ))}
        </div>

        {/* Rows */}
        <div className="mt-6 space-y-2">
          {rows.length === 0 ? (
            <div className="border border-white/10 bg-black/40 p-10 text-center"
                 data-testid="contact-inbox-empty">
              <EnvelopeOpen size={42} weight="duotone" className="mx-auto text-gray-600" />
              <div className="mt-3 text-sm text-white">No submissions here</div>
              <div className="mt-1 text-xs text-gray-500 font-mono">
                {status === "open" ? "Inbox zero — nothing to action." : "Nothing matches this filter."}
              </div>
            </div>
          ) : (
            rows.map((r) => {
              const isOpen = expanded.has(r.id);
              return (
                <div key={r.id}
                     className={`border ${r.handled ? "border-white/5" : "border-white/15"} bg-black/40`}
                     data-testid={`contact-row-${r.id}`}>
                  <button
                    onClick={() => toggle(r.id)}
                    className="w-full flex items-center gap-4 px-4 py-3 hover:bg-white/[0.02] transition text-left"
                    data-testid={`contact-row-toggle-${r.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm text-white font-bold truncate">{r.name}</span>
                        <span className="text-xs font-mono text-gray-400 truncate">&lt;{r.email}&gt;</span>
                        {r.handled ? (
                          <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase text-emerald-400 border border-emerald-400/30 px-1.5 py-0.5">
                            <CheckCircle size={10} weight="fill" /> handled
                          </span>
                        ) : (
                          <span className="text-[10px] font-mono uppercase text-[#FFCC00] border border-[#FFCC00]/40 px-1.5 py-0.5">
                            open
                          </span>
                        )}
                      </div>
                      <div className="mt-1 text-xs text-gray-500 font-mono truncate">
                        {fmtDate(r.created_at)}
                        {r.handled_by && <> · handled by {r.handled_by}</>}
                      </div>
                      {!isOpen && (
                        <div className="mt-1 text-xs text-gray-400 truncate">
                          {r.message.slice(0, 140)}{r.message.length > 140 ? "…" : ""}
                        </div>
                      )}
                    </div>
                    <span className="text-[10px] font-mono text-gray-600 uppercase shrink-0">
                      {isOpen ? "collapse" : "expand"}
                    </span>
                  </button>

                  {isOpen && (
                    <div className="border-t border-white/5 px-4 py-4">
                      <div className="whitespace-pre-wrap text-sm text-gray-200 leading-relaxed font-sans"
                           data-testid={`contact-row-message-${r.id}`}>
                        {r.message}
                      </div>
                      <div className="mt-4 flex items-center gap-2 flex-wrap">
                        <button
                          onClick={() => markHandled(r)}
                          className="btn-ghost text-xs py-2 px-3 inline-flex items-center gap-2"
                          data-testid={`contact-row-toggle-handled-${r.id}`}
                        >
                          {r.handled ? (
                            <><ArrowUUpLeft size={14} weight="bold" /> Reopen</>
                          ) : (
                            <><CheckCircle size={14} weight="fill" /> Mark handled</>
                          )}
                        </button>
                        <span className="ml-auto text-[10px] font-mono text-gray-600">
                          from <span className="text-gray-500">{r.ip || "unknown"}</span>
                        </span>
                      </div>

                      {/* v3.2.59 · Reply history + inline reply box. */}
                      {(r.replies || []).length > 0 && (
                        <div className="mt-5 space-y-2" data-testid={`contact-row-history-${r.id}`}>
                          <div className="text-[10px] font-mono uppercase tracking-widest text-gray-500">
                            // reply history
                          </div>
                          {(r.replies || []).map((rep, idx) => (
                            <div key={idx} className="border border-white/5 bg-white/[0.02] px-3 py-2">
                              <div className="text-[10px] font-mono text-gray-500">
                                {fmtDate(rep.sent_at)} · <span className="text-[#00E5FF]">{rep.sent_by}</span>
                              </div>
                              <div className="mt-1 text-xs text-gray-300 whitespace-pre-wrap">{rep.body}</div>
                            </div>
                          ))}
                        </div>
                      )}

                      <div className="mt-5">
                        <div className="text-[10px] font-mono uppercase tracking-widest text-gray-500 mb-2">
                          // reply from the app
                        </div>
                        <textarea
                          rows={4}
                          className="w-full bg-black/50 border border-white/10 focus:border-[#00E5FF]/60 focus:outline-none px-3 py-2 text-sm text-gray-100 font-sans"
                          placeholder="Type your reply. This will be emailed to the sender from admin@eengdes.com."
                          value={drafts[r.id] || ""}
                          onChange={(e) => setDraft(r.id, e.target.value)}
                          data-testid={`contact-row-reply-body-${r.id}`}
                        />
                        <div className="mt-2 flex items-center gap-2 flex-wrap">
                          <button
                            onClick={() => sendReply(r)}
                            disabled={sending.has(r.id) || (drafts[r.id] || "").trim().length < 2}
                            className="btn-primary text-xs py-2 px-3 inline-flex items-center gap-2 disabled:opacity-40"
                            data-testid={`contact-row-reply-send-${r.id}`}
                          >
                            <PaperPlaneTilt size={14} weight="fill" />
                            {sending.has(r.id) ? "Sending…" : "Send reply"}
                          </button>
                          <a
                            href={`mailto:${r.email}?subject=Re:%20your%20EEngDeS%20message&body=Hi%20${encodeURIComponent(r.name)}%2C%0A%0A`}
                            target="_blank"
                            rel="noreferrer"
                            className="text-[10px] font-mono uppercase tracking-wider text-gray-500 hover:text-[#00E5FF]"
                            data-testid={`contact-row-reply-mailto-${r.id}`}
                          >
                            or open in mail client →
                          </a>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
